import { useState } from "react";
import { toast } from "react-hot-toast";
import { ref, get, update, set, push } from "firebase/database";
import { db } from "../../Firebase/firestore";
import { X } from "lucide-react";
import "./UpdateUnderloading.css";

function UpdateUnderloading({ onClose }) {
  const [codeIds, setCodeIds] = useState("");
  const [checking, setChecking] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [checkResult, setCheckResult] = useState(null);
  const [updateResult, setUpdateResult] = useState(null);

  // Stores selected order IDs
  const [selectedOrders, setSelectedOrders] = useState([]);
 const handleClose = () => {
  handleClear();
  onClose?.();
};
  /*
   * Create timestamp:
   *
   * 2026-06-15 11:44:05.175
   *
   * Using Asia/Kolkata timezone.
   */
  const getIndiaTimestamp = () => {
    const now = new Date();

    const indiaTime = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Kolkata",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);

    const getPart = (type) =>
      indiaTime.find((part) => part.type === type)?.value || "";

    return (
      `${getPart("year")}-${getPart("month")}-${getPart("day")} ` +
      `${getPart("hour")}:${getPart("minute")}:${getPart("second")}.` +
      `${String(now.getMilliseconds()).padStart(3, "0")}`
    );
  };

  // Matches the exact structure used in UserOrders → confirmPayment
  const sendPaymentConfirmedNotification = async (
    userId,
    orderId,
    paymentAmount,
  ) => {
    try {
      const notificationRef = push(
        ref(db, `user_notifications/Order Dispatch/${userId}`),
      );

      await set(notificationRef, {
        gifUrl: "/Images/New notification.gif",
        message: `Your payment of ₹${paymentAmount} has been confirmed. Dispatch can now proceed.`,
        orderId,
        seen: false,
        timestamp: getIndiaTimestamp(),
        title: "Payment Confirmed",
      });
    } catch (error) {
      console.error("Failed to send Payment Confirmed notification:", error);
    }
  };

  // Underloading notification (same path as other order notifications)
  const sendUnderloadingNotification = async (userId, order) => {
    try {
      const notificationRef = push(
        ref(db, `user_notifications/Order Dispatch/${userId}`),
      );

      const siteAddress =
        order.siteAddress?.address ||
        (typeof order.siteAddress === "string"
          ? order.siteAddress
          : "your site");

      await set(notificationRef, {
        gifUrl: "/Images/New notification.gif",
        message: `Your order for ${order.brand || ""} ${order.grade || ""} at ${siteAddress} is currently under loading and will be dispatched shortly.`,
        orderId: order.orderId,
        seen: false,
        timestamp: getIndiaTimestamp(),
        title: "Order Underloading",
      });
    } catch (error) {
      console.error("Failed to send underloading notification:", error);
    }
  };

  /*
   * Step 1:
   * Check Customer Codes
   *
   * No Firebase update happens here.
   */
  const handleCheck = async () => {
    const enteredCodeIds = codeIds
      .split("\n")
      .map((id) => id.trim())
      .filter(Boolean);

    if (enteredCodeIds.length === 0) {
      toast.error("Please enter at least one Customer Code.");
      return;
    }

    try {
      setChecking(true);

      setCheckResult(null);
      setUpdateResult(null);
      setSelectedOrders([]);

      // Case-insensitive matching
      const codeIdsForMatching = new Set(
        enteredCodeIds.map((id) => id.toLowerCase()),
      );

      const ordersSnapshot = await get(ref(db, "orders"));
      const orders = ordersSnapshot.val();

      if (!orders) {
        setCheckResult({
          found: [],
          notFound: enteredCodeIds,
        });

        toast.error("No orders found.");
        return;
      }

      const found = [];
      const foundCodeIds = new Set();

      /*
       * Search:
       *
       * /orders/{customerUid}/{orderId}
       */
      Object.entries(orders).forEach(([customerUid, customerOrders]) => {
        if (!customerOrders || typeof customerOrders !== "object") {
          return;
        }

        Object.entries(customerOrders).forEach(([orderId, order]) => {
          if (!order || typeof order !== "object") {
            return;
          }

          /*
           * Firebase field is "code"
           */
          const orderCode = String(order.code || "").trim();

          const orderCodeForMatching = orderCode.toLowerCase();

          if (!codeIdsForMatching.has(orderCodeForMatching)) {
            return;
          }

          foundCodeIds.add(orderCodeForMatching);

          const currentStatus = String(order.status || "").trim();

          found.push({
            code: orderCode,
            orderId,
            customerUid,
            currentStatus: currentStatus || "Unknown",

            companyName: order.companyName || "",

            quantity: order.quantity || "",

            quantityType: order.quantityType || "",

            po: order.PO || "",

            canUpdate: currentStatus.toLowerCase() === "pending",
          });
        });
      });

      /*
       * Codes that do not exist
       */
      const notFound = enteredCodeIds.filter(
        (codeId) => !foundCodeIds.has(codeId.toLowerCase()),
      );

      setCheckResult({
        found,
        notFound,
      });

      if (found.length > 0) {
        toast.success(`${found.length} matching order(s) found.`);
      } else {
        toast.error("No matching Customer Codes were found.");
      }
    } catch (error) {
      console.error("Check Customer Codes Error:", error);

      toast.error(
        error?.message || "Something went wrong while checking codes.",
      );
    } finally {
      setChecking(false);
    }
  };

  /*
   * Select / unselect one order
   */
  const handleOrderSelection = (orderKey) => {
    setSelectedOrders((previous) => {
      if (previous.includes(orderKey)) {
        return previous.filter((key) => key !== orderKey);
      }

      return [...previous, orderKey];
    });
  };

  /*
   * Select all Pending orders
   */
  const handleSelectAll = () => {
    if (!checkResult?.found) return;

    const selectableOrders = checkResult.found
      .filter((item) => item.canUpdate)
      .map((item) => `${item.customerUid}_${item.orderId}`);

    const allSelected =
      selectableOrders.length > 0 &&
      selectableOrders.every((key) => selectedOrders.includes(key));

    if (allSelected) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(selectableOrders);
    }
  };

  /*
   * Step 2:
   * Confirm selected orders
   */
  const handleConfirmUpdate = async () => {
    if (selectedOrders.length === 0) {
      toast.error("Please select at least one order to update.");
      return;
    }

    const ordersToUpdate = checkResult.found.filter((item) =>
      selectedOrders.includes(`${item.customerUid}_${item.orderId}`),
    );

    if (ordersToUpdate.length === 0) {
      toast.error("No valid orders selected.");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to update ${ordersToUpdate.length} selected order(s) to "Underloading"?\n\n` +
        `Status will change to Underloading.\n` +
        `Payment status will change to confirmed.`,
    );

    if (!confirmed) return;

    try {
      setUpdating(true);

      const updates = {};
      const updated = [];
      const statusUpdatedAt = getIndiaTimestamp();

      // We need the full order data for the notification message
      // (brand, grade, siteAddress). Fetch them once.
      const fullOrdersSnapshot = await get(ref(db, "orders"));
      const allOrders = fullOrdersSnapshot.val() || {};

      ordersToUpdate.forEach((item) => {
        const orderPath = `orders/${item.customerUid}/${item.orderId}`;

        updates[`${orderPath}/status`] = "Underloading";
        updates[`${orderPath}/payment_status`] = "confirmed";
        updates[`${orderPath}/statusUpdatedAt`] = statusUpdatedAt;

        updated.push({
          code: item.code,
          orderId: item.orderId,
          customerUid: item.customerUid,
          previousStatus: item.currentStatus,
          newStatus: "Underloading",
          newPaymentStatus: "confirmed",
          statusUpdatedAt,
        });
      });

      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
      }

      // ---------- SEND NOTIFICATIONS (same as UserOrders) ----------
      for (const item of updated) {
        const fullOrder = allOrders[item.customerUid]?.[item.orderId] || {};

        // ---------- SEND NOTIFICATIONS (same as UserOrders) ----------
        for (const item of updated) {
          const fullOrder = allOrders[item.customerUid]?.[item.orderId] || {};

          const paymentAmount = Number(fullOrder.paymentAmount) || 0;

          // 1. Payment Confirmed notification
          await sendPaymentConfirmedNotification(
            item.customerUid,
            item.orderId,
            paymentAmount,
          );

          // 2. Underloading notification
          await sendUnderloadingNotification(item.customerUid, {
            ...fullOrder,
            orderId: item.orderId,
            brand: fullOrder.brand,
            grade: fullOrder.grade,
            siteAddress: fullOrder.siteAddress,
          });
        }
        // -------------------------------------------------------------
      }
      // -------------------------------------------------------------

      setUpdateResult({
        updated,
        notFound: checkResult.notFound,
      });

      // Update UI status
      setCheckResult((previous) => {
        if (!previous) return previous;
        return {
          ...previous,
          found: previous.found.map((item) => {
            const key = `${item.customerUid}_${item.orderId}`;
            if (selectedOrders.includes(key)) {
              return {
                ...item,
                currentStatus: "Underloading",
                canUpdate: false,
              };
            }
            return item;
          }),
        };
      });

      setSelectedOrders([]);
      toast.success(`${updated.length} order(s) updated successfully.`);
    } catch (error) {
      console.error("Update Underloading Error:", error);
      toast.error(
        error?.message || "Something went wrong while updating orders.",
      );
    } finally {
      setUpdating(false);
    }
  };

  /*
   * Clear everything
   */
  const handleClear = () => {
    setCodeIds("");
    setCheckResult(null);
    setUpdateResult(null);
    setSelectedOrders([]);
  };

  /*
   * Count selectable orders
   */
  const selectableCount =
    checkResult?.found?.filter((item) => item.canUpdate).length || 0;

  /*
   * Check if all selectable orders are selected
   */
  const allSelected =
    selectableCount > 0 &&
    checkResult?.found
      ?.filter((item) => item.canUpdate)
      .every((item) =>
        selectedOrders.includes(`${item.customerUid}_${item.orderId}`),
      );

  return (
 <div className="update-underloading">
  <div className="update-underloading-header">
    <h3>Update Orders</h3>

    <button
      type="button"
      className="update-underloading-close"
      onClick={handleClose}
      aria-label="Close"
      title="Close"
    >
      <X size={17} />
    </button>
  </div>

      <label htmlFor="customer-codes">Customer Codes</label>

      <textarea
        id="customer-codes"
        value={codeIds}
        onChange={(e) => setCodeIds(e.target.value)}
        placeholder={`ULTRATECH321
SHREE456
ULTRATECH789`}
        rows={5}
        disabled={checking || updating}
      />

      <small>Enter one Customer Code per line.</small>

      {/* Buttons */}

      <div className="underloading-actions">
        <button
          type="button"
          className="check-codes-btn"
          onClick={handleCheck}
          disabled={checking || updating}
        >
          {checking ? "Checking..." : "Check Customer Codes"}
        </button>

        {checkResult && (
          <button
            type="button"
            className="clear-codes-btn"
            onClick={handleClear}
            disabled={checking || updating}
          >
            Clear
          </button>
        )}
      </div>

      {/* =========================
          CHECK RESULT
      ========================= */}

      {checkResult && (
        <div className="update-result">
          <h4>Customer Code Check</h4>

          {/* Summary */}

          <div className="result-summary">
            <div className="result-count found-count">
              <span>Found</span>
              <strong>{checkResult.found?.length || 0}</strong>
            </div>

            <div className="result-count not-found-count">
              <span>Not Found</span>
              <strong>{checkResult.notFound?.length || 0}</strong>
            </div>
          </div>

          {/* =========================
              FOUND ORDERS
          ========================= */}

          {checkResult.found?.length > 0 && (
            <div className="result-section">
              <div className="found-header">
                <h5>Found Orders</h5>

                {selectableCount > 0 && (
                  <button
                    type="button"
                    className="select-all-btn"
                    onClick={handleSelectAll}
                    disabled={updating}
                  >
                    {allSelected ? "Unselect All" : "Select All"}
                  </button>
                )}
              </div>

              {checkResult.found.map((item, index) => {
                const orderKey = `${item.customerUid}_${item.orderId}`;

                const isSelected = selectedOrders.includes(orderKey);

                return (
                  <div
                    className={`found-order ${
                      isSelected ? "selected-order" : ""
                    }`}
                    key={`${item.orderId}-${index}`}
                  >
                    <div className="order-main">
                      {item.canUpdate && (
                        <label className="order-checkbox">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleOrderSelection(orderKey)}
                            disabled={updating}
                          />

                          <span></span>
                        </label>
                      )}

                      <div className="order-code">
                        <strong>{item.code}</strong>

                        <span
                          className={`status-badge ${item.currentStatus
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {item.currentStatus}
                        </span>
                      </div>
                    </div>

                    <div className="order-details">
                      {item.companyName && (
                        <span>Company: {item.companyName}</span>
                      )}

                      {item.quantity && (
                        <span>
                          Quantity: {item.quantity} {item.quantityType}
                        </span>
                      )}

                      {item.po && <span>PO: {item.po}</span>}

                      <span>Order ID: {item.orderId}</span>
                    </div>

                    {!item.canUpdate && (
                      <div className="cannot-update">
                        This order cannot be selected because its current status
                        is "{item.currentStatus}".
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* =========================
              NOT FOUND
          ========================= */}

          {checkResult.notFound?.length > 0 && (
            <div className="result-section">
              <h5>Not Found</h5>

              <div className="not-found-list">
                {checkResult.notFound.map((code, index) => (
                  <span key={`${code}-${index}`}>{code}</span>
                ))}
              </div>
            </div>
          )}

          {/* =========================
              CONFIRM UPDATE
          ========================= */}

          {selectedOrders.length > 0 && (
            <div className="selection-summary">
              <span>{selectedOrders.length} order(s) selected</span>

              <button
                type="button"
                className="confirm-update-btn"
                onClick={handleConfirmUpdate}
                disabled={updating}
              >
                {updating
                  ? "Updating..."
                  : `Confirm Update ${selectedOrders.length} Order(s)`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* =========================
          UPDATE RESULT
      ========================= */}

      {updateResult && (
        <div className="final-update-result">
          <h4>Update Completed</h4>

          <p>
            Updated: <strong>{updateResult.updated?.length || 0}</strong>
          </p>

          <p>
            Not Found: <strong>{updateResult.notFound?.length || 0}</strong>
          </p>

          {updateResult.updated?.length > 0 && (
            <div className="result-section">
              <h5>Updated Orders</h5>

              {updateResult.updated.map((item, index) => (
                <div className="updated-order" key={`${item.orderId}-${index}`}>
                  <strong>{item.code}</strong>

                  <span>
                    {item.previousStatus} → {item.newStatus}
                  </span>

                  <small>Updated at: {item.statusUpdatedAt}</small>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UpdateUnderloading;
